import { AttributeBlock, AttributeValueObject } from '@/src/interfaces';
import Modal from '@empiriecom/module-components/Modal';
import Image from '@empiriecom/module-components/Image';
import useTracking from '@empiriecom/module-components/hooks/useTracking';
import React, { FunctionComponent, ReactElement, useState } from 'react';
import { v1 as uuidv1 } from 'uuid';
import styled from 'styled-components';
import { useMedia } from 'react-media-match';
import Card from '../Card';
import { AttributeDownload } from '../ProductAttributes';

const LabelWrapper = styled.div<{ layout: 'default' | 'compact' }>`
  display: ${({ layout }) => (layout === 'compact' ? 'inline-flex;' : 'flex;')};
  justify-content: flex-start;
  align-items: center;
  padding: ${({ layout }) => (layout === 'compact' ? '0 0.772rem 0 0;' : '0 1rem;')};
  margin: 0.5rem 0;
  ${({ layout }) => layout === 'default' && 'cursor: pointer;'};
`;

const LabelImageWrapper = styled.div<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '2.188rem;' : '3.291rem;')};
  height: ${({ isMobile }) => (isMobile ? '1.25rem;' : '1.875rem;')};
`;

const EEKLabelProto = styled(Image)``;

const EEKLabel = styled(EEKLabelProto)<{ layout: 'default' | 'compact' }>`
  ${({ layout }) => layout === 'default' && 'margin-right: 0.75rem;'};
`;

const EEKImage = styled(Image)`
  width: auto;
  display: block;
  margin: 0 auto;
  max-width: 50%;
`;

const ProductDatasheetWrapper = styled.div`
  border-top: ${({ theme }) => `1px solid ${theme.greyNormal.color.normal}`};
  padding: 0.875rem 1rem;
  display: flex;
  justify-content: flex-start;
  align-items: center;
`;

interface PowerEfficiencyProps {
  attribute: AttributeBlock;
  layout?: 'default' | 'compact';
}

const PowerEfficiencyModalLabel: FunctionComponent<{
  value: AttributeValueObject;
  layout: 'default' | 'compact';
  baseAttribute: AttributeBlock;
}> = ({ value, layout = 'default', baseAttribute }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });
  const dispatchGTMEvent = useTracking();
  const { iconUrl, link, text } = value;
  const cleanedIconUrl = iconUrl?.endsWith('.png') ? iconUrl?.replace('.png', '') : iconUrl;
  return (
    <React.Fragment key={uuidv1()}>
      <LabelWrapper
        data-testid="power-efficiency-label-wrapper"
        layout={layout}
        onClick={() => {
          if (typeof link !== 'undefined') {
            setIsModalOpen(true);
            dispatchGTMEvent({
              event: 'ga_event',
              eventAction: 'viewLayer',
              eventCategory: 'noteLayer',
              eventLabel: 'detailview',
              eventValue: 'EEK Label',
            });
          }
        }}
      >
        <LabelImageWrapper isMobile={isMobile}>
          <EEKLabel
            alt={typeof text !== 'undefined' && text !== null && text !== '' ? text : ''}
            src={cleanedIconUrl?.concat('.svg')}
            fallback={() => (
              <EEKLabel
                alt={typeof text !== 'undefined' && text !== null && text !== '' ? text : ''}
                src={cleanedIconUrl?.concat('.png')}
                fallback={() => null}
                layout={layout}
              />
            )}
            data-testid="power-efficiency-label-image"
            layout={layout}
          />
        </LabelImageWrapper>
        {typeof text !== 'undefined' && text !== null && text !== '' ? text : ''}
      </LabelWrapper>
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        headline={
          typeof text !== 'undefined' && text !== null && text !== ''
            ? text
            : baseAttribute.headline
        }
      >
        <div data-testid="power-efficiency-modal-content">
          <EEKImage alt="" src={link} fallback={() => null} />
        </div>
      </Modal>
    </React.Fragment>
  );
};

/** This is the power efficiency component. It renders the given power efficiency attribute depending on the configured layout.
 *  There are two layouts available: <pre>default</pre> and <pre>compact</pre>.
 */
const PowerEfficiency: FunctionComponent<PowerEfficiencyProps> = ({
  attribute,
  layout = 'default',
}): ReactElement => {
  const isMobile = useMedia({ desktop: false, mobile: true, tablet: false });

  if (layout === 'compact') {
    return (
      <div data-testid="compact-power-efficiency-container">
        {attribute.values.map((value) => {
          switch (value.type) {
            case 'powerEfficiency':
              return value.values.map((val) => {
                const { iconUrl, text } = val as AttributeValueObject;
                const cleanedIconUrl = iconUrl?.endsWith('.png')
                  ? iconUrl?.replace('.png', '')
                  : iconUrl;
                return (
                  <React.Fragment key={uuidv1()}>
                    <LabelWrapper
                      data-testid="compact-power-efficiency-label-wrapper"
                      layout={layout}
                    >
                      <LabelImageWrapper isMobile={isMobile}>
                        <EEKLabel
                          alt={
                            typeof text !== 'undefined' && text !== null && text !== '' ? text : ''
                          }
                          src={cleanedIconUrl?.concat('.svg')}
                          fallback={() => (
                            <EEKLabel
                              alt={
                                typeof text !== 'undefined' && text !== null && text !== ''
                                  ? text
                                  : ''
                              }
                              src={cleanedIconUrl?.concat('.png')}
                              fallback={() => null}
                              layout={layout}
                            />
                          )}
                          data-testid="compact-power-efficiency-label-image"
                          layout={layout}
                        />
                      </LabelImageWrapper>
                    </LabelWrapper>
                  </React.Fragment>
                );
              });
            default:
              return null;
          }
        })}
      </div>
    );
  }
  return (
    <Card headline={attribute.headline}>
      <div data-testid="power-efficiency-container">
        {attribute.values.map((value) => {
          switch (value.type) {
            case 'powerEfficiency':
              return value.values.map((val) => (
                <PowerEfficiencyModalLabel
                  value={val as AttributeValueObject}
                  layout={layout}
                  baseAttribute={attribute}
                />
              ));
            case 'productDataSheet':
            case 'download':
              return (
                <ProductDatasheetWrapper
                  data-testid="power-efficiency-product-data-sheet"
                  key={uuidv1()}
                >
                  <AttributeDownload values={value.values} renderIcon />
                </ProductDatasheetWrapper>
              );
            default:
              return null;
          }
        })}
      </div>
    </Card>
  );
};

export default PowerEfficiency;
